import { useRef, useState } from "react";
import { ImageOff, Loader2, Star, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { cdnConfigured, mediaUrl } from "@/lib/media";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { toast } from "@/components/ui/toast";
import {
  deleteProductImage,
  setPrimaryImage,
  uploadProductImage,
  validateImageFile,
} from "./imageApi";

export interface ProductImage {
  id: string;
  storage_key: string;
  alt_text: string | null;
  is_primary: boolean;
}

/**
 * Las fotos son lo primero que mira quien compra, asi que la subida tiene que
 * ser un gesto: arrastrar y soltar. Cada archivo sube por su cuenta y muestra
 * su propio estado; que una foto falle no cancela las demas.
 */
export function ImagesPanel({
  organizationId,
  productId,
  images,
  onChanged,
}: {
  organizationId: string;
  productId: string;
  images: ProductImage[];
  onChanged: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  async function handleFiles(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;

    // El formato y el peso se comprueban antes de pedir nada al servidor: no
    // tiene sentido gastar una firma en un archivo que va a rebotar.
    const valid: File[] = [];
    for (const file of files) {
      const problem = validateImageFile(file);
      if (problem) toast.error(`${file.name}: ${friendlyError(new Error(problem))}`);
      else valid.push(file);
    }
    if (valid.length === 0) return;

    setPending((n) => n + valid.length);

    let subidas = 0;
    for (const file of valid) {
      try {
        await uploadProductImage(organizationId, productId, file);
        subidas += 1;
      } catch (error) {
        toast.error(`${file.name}: ${friendlyError(error)}`);
      } finally {
        setPending((n) => n - 1);
      }
    }

    if (subidas > 0) {
      toast.ok(subidas === 1 ? "Imagen subida." : `${subidas} imágenes subidas.`);
      onChanged();
    }
  }

  async function handlePrimary(imageId: string) {
    setBusyId(imageId);
    try {
      await setPrimaryImage(productId, imageId);
      onChanged();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(imageId: string) {
    setBusyId(imageId);
    try {
      await deleteProductImage(organizationId, imageId);
      toast.ok("Imagen quitada.");
      onChanged();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setBusyId(null);
      setConfirming(null);
    }
  }

  return (
    <Panel
      title="Imágenes"
      actions={
        <Button
          size="sm"
          variant="secondary"
          icon={<Upload className="size-3.5" />}
          onClick={() => inputRef.current?.click()}
          loading={pending > 0}
        >
          Subir
        </Button>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(e) => {
          void handleFiles(e.target.files);
          // Sin esto, elegir el mismo archivo dos veces seguidas no dispara
          // el evento y parece que el boton no responde.
          e.target.value = "";
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          "rounded-md border border-dashed transition-colors",
          dragging ? "border-accent bg-accent-soft" : "border-line",
          images.length === 0 || pending > 0 ? "p-4" : "p-2",
        )}
      >
        {images.length === 0 && pending === 0 ? (
          <div className="flex flex-col items-center gap-2 py-3 text-center">
            <ImageOff className="size-5 text-ink-3" />
            <p className="text-ui text-ink-2">Sin imágenes</p>
            <p className="max-w-[32ch] text-xs text-ink-3">
              Arrastra las fotos aquí o pulsa Subir. JPG, PNG, WebP o AVIF, hasta 5 MB.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {images.map((img) => (
              <Tile
                key={img.id}
                image={img}
                busy={busyId === img.id}
                confirming={confirming === img.id}
                onPrimary={() => void handlePrimary(img.id)}
                onAskDelete={() => setConfirming(img.id)}
                onCancelDelete={() => setConfirming(null)}
                onDelete={() => void handleDelete(img.id)}
              />
            ))}

            {Array.from({ length: pending }).map((_, i) => (
              <div
                key={`subiendo-${i}`}
                className="flex aspect-square items-center justify-center rounded-md border border-line bg-sunk"
              >
                <Loader2 className="size-4 animate-spin text-ink-3" />
              </div>
            ))}
          </div>
        )}
      </div>

      {images.length > 0 && (
        <p className="mt-2 text-xs text-ink-3">
          La marcada con estrella es la portada: es la que sale en el catálogo y al
          compartir el enlace.
        </p>
      )}

      {!cdnConfigured && (
        <p className="mt-2 text-xs text-warn">
          Falta VITE_CDN_BASE_URL: las fotos se guardan, pero aquí no se pueden
          previsualizar.
        </p>
      )}
    </Panel>
  );
}

function Tile({
  image,
  busy,
  confirming,
  onPrimary,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  image: ProductImage;
  busy: boolean;
  confirming: boolean;
  onPrimary: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}) {
  const src = mediaUrl(image.storage_key, 240);

  return (
    <div className="group relative aspect-square overflow-hidden rounded-md border border-line bg-sunk">
      {src ? (
        <img
          src={src}
          alt={image.alt_text ?? ""}
          loading="lazy"
          className="size-full object-cover"
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <ImageOff className="size-4 text-ink-3" />
        </div>
      )}

      {busy && (
        <div className="absolute inset-0 grid place-items-center bg-surface/70">
          <Loader2 className="size-4 animate-spin text-ink-2" />
        </div>
      )}

      {confirming ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-surface/95 p-2 text-center">
          <p className="text-xs text-ink-2">¿Quitar esta foto?</p>
          <div className="flex gap-1.5">
            <Button size="sm" variant="danger" onClick={onDelete}>
              Quitar
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelDelete}>
              No
            </Button>
          </div>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onPrimary}
            disabled={image.is_primary}
            title={image.is_primary ? "Es la portada" : "Usar como portada"}
            aria-label={image.is_primary ? "Es la portada" : "Usar como portada"}
            className={cn(
              "absolute left-1 top-1 rounded p-1 transition-opacity",
              image.is_primary
                ? "bg-surface/85 opacity-100"
                : "bg-surface/85 opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            )}
          >
            <Star
              className={cn(
                "size-3.5",
                image.is_primary ? "fill-accent text-accent" : "text-ink-2",
              )}
            />
          </button>

          <button
            type="button"
            onClick={onAskDelete}
            title="Quitar"
            aria-label="Quitar imagen"
            className="absolute right-1 top-1 rounded bg-surface/85 p-1 text-ink-2 opacity-0 transition-opacity hover:text-crit group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Trash2 className="size-3.5" />
          </button>
        </>
      )}
    </div>
  );
}
